import { alpha, type Theme } from "@mui/material/styles";
import {
  getCoolestLiveHeatmapColor,
  LIVE_HEATMAP_CONFIG,
} from "@/features/deployment-dashboard/live-view/config/live-heatmap-config";
import type {
  LiveTrackPoint,
  LiveTrailSegment,
  ObservationAreaBoundary,
  SensorMarkerPoint,
  SensorStatus,
} from "@/features/deployment-dashboard/types";
import { getFacilitySchematicMapName } from "@/features/deployment-dashboard/widgets/panels/facility-schematic/register-facility-schematic-map";
import type {
  GeoScatterBoundary,
  GeoScatterChartConfig,
  GeoLineSegment,
  GeoScatterPoint,
} from "@/shared/charts";

/**
 * Zoom bounds for the facility schematic: never zoom out past the schematic
 * fitting edge-to-edge top-to-bottom or left-to-right (`min: 1`), and cap
 * zoom-in at 2x (`max: 2`).
 */
const FACILITY_SCHEMATIC_ZOOM_LIMIT = { min: 1, max: 3 };

const OBSERVATION_AREA_FILL_OPACITY = 0.15;

/** Chart config for the selected deployment's facility schematic. */
export function createFacilitySchematicChartConfig(): GeoScatterChartConfig {
  const coolestHeatmapColor = getCoolestLiveHeatmapColor(LIVE_HEATMAP_CONFIG);

  return {
    mapName: getFacilitySchematicMapName(),
    ariaLabel: "Operational facility schematic",
    zoomLimit: FACILITY_SCHEMATIC_ZOOM_LIMIT,
    boundaryStyle: {
      fill: alpha(coolestHeatmapColor, OBSERVATION_AREA_FILL_OPACITY),
    },
  };
}

function buildLiveTrackPoints(liveTracks: LiveTrackPoint[], theme: Theme): GeoScatterPoint[] {
  const color = theme.palette.common.white;

  return liveTracks.map((point) => ({
    id: point.id,
    position: [point.x, point.y],
    footprint: point.footprint,
    color: alpha(color, 0.85),
    borderColor: alpha(theme.palette.common.white, 0.6),
    borderWidth: 1,
  }));
}

function buildTrailSegments(trails: LiveTrailSegment[], theme: Theme): GeoLineSegment[] {
  return trails.map((trail) => ({
    id: trail.id,
    start: trail.start,
    end: trail.end,
    color: alpha(theme.palette.common.white, 0.55 * trail.opacity),
    width: 3.5,
  }));
}

function getSensorStatusColor(status: SensorStatus, theme: Theme): string {
  switch (status) {
    case "ONLINE":
      return theme.palette.success.main;
    case "MAINTENANCE":
      return theme.palette.warning.main;
    case "OFFLINE":
      return theme.palette.error.main;
  }
}

function escapeTooltipText(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

/**
 * Finds the observation area that contains the given point using ray-casting.
 */
function findContainingArea(
  point: { x: number; y: number },
  areas: ObservationAreaBoundary[]
): ObservationAreaBoundary | null {
  for (const area of areas) {
    if (area.points.length < 3) continue;

    let inside = false;
    for (
      let index = 0, previousIndex = area.points.length - 1;
      index < area.points.length;
      previousIndex = index++
    ) {
      // eslint-disable-next-line security/detect-object-injection
      const vertex = area.points[index];
      // eslint-disable-next-line security/detect-object-injection
      const previousVertex = area.points[previousIndex];

      const crossesHorizontalRay =
        vertex.y > point.y !== previousVertex.y > point.y &&
        point.x <
          ((previousVertex.x - vertex.x) * (point.y - vertex.y)) / (previousVertex.y - vertex.y) +
            vertex.x;

      if (crossesHorizontalRay) inside = !inside;
    }

    if (inside) return area;
  }

  return null;
}

/**
 * Calculates the vertical center of an observation area.
 */
function getAreaVerticalCenter(area: ObservationAreaBoundary): number {
  if (area.points.length === 0) return 0;

  const yValues = area.points.map((p) => p.y);
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);

  return (minY + maxY) / 2;
}

function buildSensorPoints(
  sensorPoints: SensorMarkerPoint[],
  theme: Theme,
  observationAreas: ObservationAreaBoundary[],
  floorplanHeight: number
): GeoScatterPoint[] {
  return sensorPoints.map((sensor) => {
    const statusColor = getSensorStatusColor(sensor.status, theme);

    // Determine label position based on observation area center
    const containingArea = findContainingArea({ x: sensor.x, y: sensor.y }, observationAreas);
    const referenceY = containingArea ? getAreaVerticalCenter(containingArea) : floorplanHeight / 2;
    const labelPosition = sensor.y < referenceY ? "top" : "bottom";

    return {
      id: sensor.id,
      name: `${sensor.name} ${sensor.status}`,
      position: [sensor.x, sensor.y],
      color: statusColor,
      borderColor: alpha(theme.palette.common.white, 0.7),
      borderWidth: 1.125,
      tooltip: `<strong>${escapeTooltipText(sensor.name)}</strong><br/>${sensor.status}`,
      symbol: "rect",
      symbolSize: 14,
      label: {
        primaryText: sensor.name,
        secondaryText: sensor.status,
        secondaryColor: statusColor,
        scale: 0.75,
        position: labelPosition,
      },
    };
  });
}

/**
 * Maps observation areas into shared boundary shapes. The chart-level boundary
 * style supplies the shared observation-area fill/outline look, so the
 * schematic only passes geometry and labels here.
 *
 * Kept as its own memoizable step (rather than folded into
 * createFacilitySchematicMarkers) so its result only changes when
 * observationAreas itself changes, not on every live-track tick; the geo
 * chart uses that referential stability to know when to re-focus its zoom.
 */
export function createObservationAreaBoundaries(
  observationAreas: ObservationAreaBoundary[],
  tooltipsEnabled = true
): GeoScatterBoundary[] {
  return observationAreas.map((area) => ({
    id: area.id,
    name: area.name,
    ...(tooltipsEnabled ? { tooltip: area.name } : {}),
    points: area.points.map((point): [number, number] => [point.x, point.y]),
    // A whole-floor tint should sit behind the heatmap/detections, not over them.
    layer: "background" as const,
  }));
}

/**
 * Maps facility schematic domain data (live tracks, sensors) into shared
 * scatter shapes. There are no notification markers here: the notification
 * stream contract carries no coordinates, so notifications are never plotted
 * on the schematic.
 */
export function createFacilitySchematicMarkers(
  liveTracks: LiveTrackPoint[],
  sensorPoints: SensorMarkerPoint[],
  theme: Theme,
  trails: LiveTrailSegment[] = [],
  observationAreas: ObservationAreaBoundary[] = [],
  floorplanHeight = 0
): { points: GeoScatterPoint[]; lineSegments: GeoLineSegment[] } {
  return {
    points: [
      ...buildLiveTrackPoints(liveTracks, theme),
      ...buildSensorPoints(sensorPoints, theme, observationAreas, floorplanHeight),
    ],
    lineSegments: buildTrailSegments(trails, theme),
  };
}
